import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container } from '@mui/material'
import { useDatasources } from '@/services/datasources/datasources.service'
import { DatasourceFilters } from '@/services/datasources/types'
import Header from '../header'
import TableComponent from '@/components/table'
import { HeadCell } from '@/components/table/auxiliar/types'
import Button from '@/components/ui/button'
import Input from '@/components/ui/input'
import { Pagination } from '@/components/pagination'
import {
  FiltersSection,
  StatusBadge,
  LoadingWrapper,
  EmptyState,
  ErrorState,
  TableWrapper,
  ActionsCell,
  TypeBadge,
} from './styled'

const Datasources: React.FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<DatasourceFilters>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string | undefined>()

  const { data, isLoading, error } = useDatasources({
    page,
    limit: 10,
    filters,
  })

  const handleSearch = () => {
    setFilters({ search: searchTerm, type: selectedType })
    setPage(1)
  }

  const handleTypeFilter = (type?: string) => {
    setSelectedType(type)
  }

  const handleDatasourceClick = (datasourceId: string) => {
    navigate(`/datasource/${datasourceId}`)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return '#4BA765'
      case 'disconnected':
        return '#FF9800'
      case 'error':
        return '#FF4444'
      case 'pending':
        return '#2196F3'
      default:
        return '#666666'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'snowflake':
        return '#29B5E8'
      case 'bloomberg':
        return '#FF6B00'
      case 'datadog':
        return '#632CA6'
      case 'product_catalog':
        return '#1976D2'
      case 's3':
        return '#FF9900'
      case 'api':
        return '#4CAF50'
      default:
        return '#666666'
    }
  }

  const formatType = (type: string) => {
    return type.replace(/_/g, ' ').toUpperCase()
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ padding: '30px' }}>
        <ErrorState>
          <h3>Error loading datasources</h3>
          <p>{error instanceof Error ? error.message : 'An error occurred'}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </ErrorState>
      </Container>
    )
  }

  // Table headers configuration
  const headers: HeadCell[] = [
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: 'Name',
      maxCharactersWidth: 40,
    },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: 'Type',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'lastSync',
      numeric: false,
      disablePadding: false,
      label: 'Last Sync',
    },
    {
      id: 'usage',
      numeric: true,
      disablePadding: false,
      label: 'Usage (30d)',
    },
    {
      id: 'created',
      numeric: false,
      disablePadding: false,
      label: 'Created',
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: 'Actions',
      notSortable: true,
    },
  ]

  // Transform data for table
  const tableData =
    data?.datasources.map((datasource) => ({
      name: (
        <span
          style={{ cursor: 'pointer', color: '#1976d2' }}
          onClick={() => handleDatasourceClick(datasource.id)}
        >
          {datasource.name}
        </span>
      ),
      type: (
        <TypeBadge color={getTypeColor(datasource.type)}>
          {formatType(datasource.type)}
        </TypeBadge>
      ),
      status: (
        <StatusBadge color={getStatusColor(datasource.connectionStatus)}>
          {datasource.connectionStatus}
        </StatusBadge>
      ),
      lastSync: datasource.lastConnected
        ? new Date(datasource.lastConnected).toLocaleString()
        : 'Never',
      usage: datasource.usageStats?.totalQueries || 0,
      created: new Date(datasource.createdAt).toLocaleDateString(),
      actions: (
        <ActionsCell>
          <Button
            variant="secondary"
            onClick={() => handleDatasourceClick(datasource.id)}
          >
            View Details
          </Button>
        </ActionsCell>
      ),
    })) || []

  return (
    <Container maxWidth="xl" sx={{ padding: '30px' }}>
      <Header
        title="Data Sources"
        description="Manage and monitor your connected data sources"
      >
        <Button variant="primary" onClick={() => navigate('/datasource/new')}>
          Connect New Source
        </Button>
      </Header>

      <FiltersSection>
        <div className="filters-row">
          <div className="filter-buttons">
            <Button
              variant={!selectedType ? 'primary' : 'secondary'}
              onClick={() => handleTypeFilter(undefined)}
            >
              All Types
            </Button>
            <Button
              variant={selectedType === 'snowflake' ? 'primary' : 'secondary'}
              onClick={() => handleTypeFilter('snowflake')}
            >
              Snowflake
            </Button>
            <Button
              variant={selectedType === 'bloomberg' ? 'primary' : 'secondary'}
              onClick={() => handleTypeFilter('bloomberg')}
            >
              Bloomberg
            </Button>
            <Button
              variant={selectedType === 'datadog' ? 'primary' : 'secondary'}
              onClick={() => handleTypeFilter('datadog')}
            >
              Datadog
            </Button>
            <Button
              variant={selectedType === 'api' ? 'primary' : 'secondary'}
              onClick={() => handleTypeFilter('api')}
            >
              API
            </Button>
          </div>

          <div className="search-section">
            <Input
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} width="auto" height="54px">
              Search
            </Button>
          </div>
        </div>
      </FiltersSection>

      {isLoading ? (
        <LoadingWrapper>
          <p>Loading datasources...</p>
        </LoadingWrapper>
      ) : data?.datasources.length === 0 ? (
        <EmptyState>
          <h3>No datasources found</h3>
          <p>Connect your first data source to get started</p>
          <Button variant="primary" onClick={() => navigate('/datasource/new')}>
            Connect Data Source
          </Button>
        </EmptyState>
      ) : (
        <TableWrapper>
          <TableComponent headers={headers} rows={tableData} />

          {data && data.total > data.limit && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(data.total / data.limit)}
              onPageChange={setPage}
            />
          )}
        </TableWrapper>
      )}
    </Container>
  )
}

export default Datasources
